import moment from "moment-timezone";
import { DateConstraint } from "../../../types/fieldInstance.interface";
import { PLACEHOLDER_VALUE } from "../../../shared/sharedConstants";

export function addTimezoneInfo(date: string) {
  const dateObj = new Date(date);
  return dateObj.toISOString();
}

/**
 * Lower bound for every date picker: a date may not be set further back than
 * MIN_YEARS_BACK years from today. This is a BUSINESS rule (MARCC-689) — a date
 * older than that is treated as a mistyped year (e.g. typing "26", which the
 * native input zero-pads to "0026") rather than a real entry. It replaces the
 * old Salesforce technical lower bound of year 1700.
 *
 * The upper bound stays at Salesforce's hard ceiling (year 4000): values above
 * it fail on upsert with FIELD_INTEGRITY_EXCEPTION and the incident never saves.
 * Future dates are separately blocked per-field by the "not in the future"
 * constraint, which keeps its own message.
 *
 * See docs/datetime-fields.md for the full root-cause writeup.
 */
export const MIN_YEARS_BACK = 5;
export const SF_MAX_YEAR = 4000;
// `datetime-local` min/max attribute format: YYYY-MM-DDTHH:mm
export const SF_MAX_DATETIME = `${SF_MAX_YEAR}-12-31T23:59`;
export const UNSUPPORTED_DATE_RANGE_MESSAGE = "Enter Correct Date";

/**
 * Earliest datetime any date field may hold: midnight, MIN_YEARS_BACK years ago.
 * Computed from "now" on each call so the bound tracks the current date rather
 * than going stale at import time. Uses start-of-day so a date exactly 5 years
 * back is allowed regardless of the time of day entered.
 */
export function getMinSupportedDate(): Date {
  return moment().subtract(MIN_YEARS_BACK, "years").startOf("day").toDate();
}

/** The picker's `min` attribute (YYYY-MM-DDTHH:mm) for the 5-years-back bound. */
export function getMinSupportedDatetimeLocal(): string {
  return moment(getMinSupportedDate()).format("YYYY-MM-DDTHH:mm");
}

/**
 * True when `value` parses to a real date no older than MIN_YEARS_BACK years and
 * whose year Salesforce can still store (≤ SF_MAX_YEAR). Blank/empty is treated
 * as valid here — required-ness is enforced separately by the field schema. Used
 * both to surface an inline validation error and to keep an out-of-range value
 * from being submitted.
 *
 * The ceiling is read with `getFullYear()` (local), not `getUTCFullYear()`: the
 * value originates from a local-time `datetime-local` entry, so reading it in UTC
 * could roll the year forward at the boundary (e.g. 4000-12-31 23:59 local →
 * 4001 UTC in a negative offset) and reject a value the picker's local `max`
 * allowed. Local-year keeps validation consistent with the input.
 */
export function isWithinSupportedDateRange(
  value: string | null | undefined
): boolean {
  if (!value) return true;
  const date = new Date(value);
  if (isNaN(date.getTime())) return false;
  if (date.getFullYear() > SF_MAX_YEAR) return false;
  return date.getTime() >= getMinSupportedDate().getTime();
}

export function formatDateTime(date: string) {
  if (!date) return null;
  const dateString = date.includes("+") ? date.split("+")[0] + "Z" : date;
  const dateObj = new Date(dateString);
  return moment(dateObj).tz("America/New_York").format("YYYY-MM-DDTHH:mm");
}

/**
 *
 * @param date Removes the Z or the +/- and any following characters
 * from the end of the date string to return a date string without a timezone
 * @returns Date string without a timezone in the format of YYYY-MM-DDTHH:mm
 */
export function formatDateTimeWithoutTimezone(date: string) {
  return moment.parseZone(date).format("YYYY-MM-DDTHH:mm");
}

// Note: 'hh' is 12-hour format, 'A' is AM/PM. Use 'HH' for 24-hour format.
export const DISPLAY_DATE_TIME_FORMAT = "MMM DD, YYYY hh:mm A";

export function formatDisplayDateTime(
  date: string | null | undefined,
  fallback: string = PLACEHOLDER_VALUE
) {
  if (!date) return fallback;

  const strictMoment = moment(
    date,
    [
      moment.ISO_8601,
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY",
    ],
    true
  );

  if (strictMoment.isValid()) {
    return strictMoment.format(DISPLAY_DATE_TIME_FORMAT);
  }

  const looseMoment = moment(date);
  return looseMoment.isValid()
    ? looseMoment.format(DISPLAY_DATE_TIME_FORMAT)
    : fallback;
}

export function replaceConstraintDate(
  value: string,
  incidentCreationDate: string | undefined,
  incidentDateTime: string | undefined
) {
  let date: string | undefined = "";
  switch (value) {
    case "creation_date":
      date =
        incidentCreationDate || moment().local().format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Incident_Date_Time__c":
      date = incidentDateTime;
      break;
    default:
      break;
  }
  return date;
}

export function getConstraint(
  constraint: DateConstraint,
  incidentCreationDate: string | undefined,
  incidentDateTime: string | undefined
) {
  if (constraint) {
    const updatedConstraint: DateConstraint = {};
    const { min, max } = constraint;

    if (min) {
      updatedConstraint.min = replaceConstraintDate(
        min,
        incidentCreationDate,
        incidentDateTime
      );
      updatedConstraint.max = moment().local().format("YYYY-MM-DD HH:mm:ss");
    }

    if (max) {
      updatedConstraint.max =
        replaceConstraintDate(max, incidentCreationDate, incidentDateTime) ??
        moment().local().format("YYYY-MM-DD HH:mm:ss");
    }

    return updatedConstraint;
  }
  return null;
}

export function getConstraintDateString(
  constraint: DateConstraint,
  valueKey: keyof typeof constraint
): string {
  return constraint[valueKey]
    ? moment(constraint[valueKey]).format("YYYY-MM-DDTHH:mm")
    : "";
}
